import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Search,
  FileText,
  Plus,
  MoreVertical,
  Download,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ArrowDownUp,
  ArrowUpAZ,
  ArrowDownZA,
  ArrowUp01,
  ArrowDown10,
  CalendarArrowUp,
  CalendarArrowDown,
  Check,
  Loader2,
  MessageSquareOff,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { fileUploadService } from '../../services/fileUploadService';
import { canEditKnowledgeBase } from '../../utils/knowledgeBasePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { KnowledgeBase } from '../../services/knowledgeBaseService';
import { FileUploadDialog } from './FileUploadDialog';
import { useFileProcessingPolling } from '../../hooks/useFileProcessingPolling';
import { formatDateWithoutYear } from '../../utils/dateFormat';

type FileSortField = 'name' | 'date' | 'size';
type FileSortOrder = 'asc' | 'desc';

interface KnowledgeBaseFile {
  id: string;
  file_id?: string; // 文件ID，用于轮询处理状态
  name: string;
  type: 'file' | 'folder';
  author?: string;
  upload_date: string;
  status: 'uploaded' | 'processing' | 'indexed' | 'error';
  size?: number;
  description?: string;
  file_type?: 'pdf' | 'doc' | 'docx' | 'txt' | 'md' | 'xlsx' | 'xls' | 'csv' | 'json' | 'xml';
  page_count?: number;
  chunk_count?: number;
  // Article metadata fields
  title?: string;
  authors?: string[];
  journal?: string;
  year?: number;
  abstract?: string;
  keywords?: string[];
}

interface FileListPanelProps {
  files: KnowledgeBaseFile[];
  loading: boolean;
  selectedFileIds: string[];
  onFileSelect: (fileId: string, selected: boolean) => void;
  onFileSelectAll: (selected: boolean, selectableFileIds?: string[]) => void;
  onSortChange: (field: FileSortField) => void;
  onSearchChange: (query: string) => void;
  searchQuery: string;
  sortField: FileSortField;
  sortOrder: FileSortOrder;
  onRefresh: () => void;
  knowledgeBase: KnowledgeBase | null;
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
  onPageChange: (page: number) => void;
  // 新增：上传完成的回调，包含成功和失败的文件信息
  onUploadBatchComplete?: (results: {
    success: string[];
    failed: Array<{ fileName: string; message: string }>;
  }) => void;
}

export const FileListPanel: React.FC<FileListPanelProps> = ({
  files,
  loading,
  selectedFileIds,
  onFileSelect,
  onFileSelectAll,
  onSortChange,
  onSearchChange,
  searchQuery,
  sortField,
  sortOrder,
  onRefresh,
  knowledgeBase,
  pagination,
  onPageChange,
  onUploadBatchComplete,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [hoveredFileId, setHoveredFileId] = useState<string | null>(null);
  const [errorTooltipFileId, setErrorTooltipFileId] = useState<string | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement>(null);

  // 本地状态：跟踪文件的实时处理状态（从 /progress API 返回）
  const [fileProcessingStatus, setFileProcessingStatus] = useState<
    Map<string, 'processing' | 'completed' | 'error'>
  >(new Map());

  // 轮询处理中的文件（只包括 processing 状态的文件）
  const processingFiles = useMemo(() => {
    return files
      .filter(f => f.status === 'processing' && f.file_id)
      .map(f => ({ id: f.id, fileId: f.file_id! }));
  }, [files]);

  // 使用轮询 hook
  useFileProcessingPolling({
    processingFiles,
    enabled: true,
    onFileStatusUpdate: (fileId, status, _data) => {
      // 更新本地状态，实时反映文件处理状态
      setFileProcessingStatus(prev => new Map(prev).set(fileId, status));

      // 只有 status 是 processing 时才继续轮询
      return status === 'processing';
    },
    onAllFilesCompleted: () => {
      // 所有文件处理完成，刷新文件列表获取完整状态
      onRefresh();
      // 清空本地状态
      setFileProcessingStatus(new Map());
    },
  });

  // 当 files 列表刷新时，重置本地状态
  useEffect(() => {
    setFileProcessingStatus(new Map());
  }, [files]);

  // 计算可选中的文件数量（排除处理中和处理失败的）
  const selectableFiles = files.filter(f => {
    const pollingStatus = f.file_id ? fileProcessingStatus.get(f.file_id) : undefined;
    const isProcessing = pollingStatus === 'processing' || f.status === 'processing';
    const hasError = f.status === 'error' || pollingStatus === 'error';
    return !isProcessing && !hasError;
  });
  const allSelectableSelected =
    selectableFiles.length > 0 && selectedFileIds.length === selectableFiles.length;
  const someSelected =
    selectedFileIds.length > 0 && selectedFileIds.length < selectableFiles.length;

  const getSortIcon = () => {
    switch (sortField) {
      case 'name':
        return sortOrder === 'asc' ? ArrowUpAZ : ArrowDownZA;
      case 'size':
        return sortOrder === 'asc' ? ArrowUp01 : ArrowDown10;
      case 'date':
        return sortOrder === 'asc' ? CalendarArrowUp : CalendarArrowDown;
      default:
        return ArrowDownUp;
    }
  };

  const SortIcon = getSortIcon();

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(size < 10 ? 1 : 0)}${units[unitIndex]}`;
  };

  const getFileTypeDisplay = (fileType?: string) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return 'PDF';
      case 'docx':
      case 'doc':
        return 'DOC';
      case 'xlsx':
      case 'xls':
        return 'XLS';
      default:
        return 'FILE';
    }
  };

  const getFileTypeColor = (fileType?: string) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return 'bg-red-50 text-red-600 border-red-200';
      case 'docx':
      case 'doc':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'xlsx':
      case 'xls':
        return 'bg-green-50 text-green-600 border-green-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const handleDownloadClick = async (file: KnowledgeBaseFile) => {
    if (!knowledgeBase) return;

    try {
      const success = await fileUploadService.downloadFile(knowledgeBase.id, file.id, file.name);
      if (success) {
        toast.success(`${file.name} ${t('knowledge.files.downloadSuccess')}`);
      }
      // HTTP errors are handled by global error handler (toast notification)
    } catch (err) {
      console.error('Failed to download file:', err);
      // HTTP errors are handled by global error handler (toast notification)
    }
    setHoveredFileId(null);
  };

  const handleDeleteClick = async (file: KnowledgeBaseFile) => {
    if (!knowledgeBase) return;

    try {
      const success = await fileUploadService.deleteFile(knowledgeBase.id, file.id);
      if (success) {
        onRefresh();
        toast.success(t('knowledge.files.deleteSuccess'));
      }
      // HTTP errors are handled by global error handler (toast notification)
    } catch (err) {
      console.error('Failed to delete file:', err);
      // HTTP errors are handled by global error handler (toast notification)
    }
    setHoveredFileId(null);
  };

  // 上传单个文件完成（乐观更新 - 当前未使用，保留接口以备后用）
  const handleUploadSingleComplete = (_file: File) => {
    // 当前不需要乐观更新，因为：
    // 1. 乐观更新的文件会干扰重复检测
    // 2. 服务器刷新后会自动获取最新的文件列表
    // 如果需要乐观更新，应该在对话框内部维护临时状态，而不是在文件列表中
  };

  // 批量上传完成
  const handleUploadBatchComplete = (results: {
    success: string[];
    failed: Array<{ fileName: string; message: string }>;
  }) => {
    // 刷新文件列表获取最新数据
    onRefresh();

    // 调用父组件回调
    onUploadBatchComplete?.(results);

    // 显示结果提示
    if (results.success.length > 0) {
      if (results.failed.length > 0) {
        toast.success(
          t('knowledge.files.upload.uploadSuccessWithFailed', {
            count: results.success.length,
            failed: results.failed.length,
          })
        );
      } else {
        toast.success(t('knowledge.files.upload.uploadSuccess', { count: results.success.length }));
      }
    }
    if (results.failed.length > 0) {
      results.failed.forEach(({ fileName, message }) => {
        toast.error(`${fileName}: ${message}`);
      });
    }
  };

  // Close sort menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
        setShowSortMenu(false);
      }
    };

    if (showSortMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortMenu]);

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='p-4 border-b border-gray-200'>
        <div className='flex items-center justify-between mb-3'>
          <h2 className='font-semibold text-gray-900 text-sm'>{t('knowledge.files.sources')}</h2>
          <span className='text-xs text-gray-500'>
            {pagination.total} {t('knowledge.files.filesCount')}
          </span>
        </div>

        {/* Search */}
        <div className='relative mb-2'>
          <Search
            className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400'
            size={16}
          />
          <input
            type='text'
            placeholder={t('knowledge.files.searchPlaceholder')}
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className='w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
        </div>

        {/* Sort Dropdown */}
        <div className='relative z-50' ref={sortMenuRef}>
          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            className='w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200'
          >
            <SortIcon size={16} className='text-gray-500' />
            <span className='flex-1 text-left'>
              {[
                { value: 'name' as const, label: t('knowledge.sort.name') },
                { value: 'date' as const, label: t('knowledge.sort.lastUpdated') },
                { value: 'size' as const, label: t('knowledge.files.size') },
              ].find(opt => opt.value === sortField)?.label || t('knowledge.sort.name')}
            </span>
          </button>

          {/* Sort Dropdown Menu */}
          {showSortMenu && (
            <div className='absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50'>
              {[
                {
                  value: 'name' as const,
                  label: t('knowledge.sort.name'),
                  icon: sortOrder === 'asc' ? ArrowUpAZ : ArrowDownZA,
                },
                {
                  value: 'date' as const,
                  label: t('knowledge.sort.lastUpdated'),
                  icon: sortOrder === 'asc' ? CalendarArrowUp : CalendarArrowDown,
                },
                {
                  value: 'size' as const,
                  label: t('knowledge.files.size'),
                  icon: sortOrder === 'asc' ? ArrowUp01 : ArrowDown10,
                },
              ].map(option => (
                <button
                  key={option.value}
                  onClick={e => {
                    e.stopPropagation();
                    onSortChange(option.value);
                    setShowSortMenu(false);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    sortField === option.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  <option.icon
                    size={16}
                    className={sortField === option.value ? 'text-blue-600' : 'text-gray-500'}
                  />
                  <span>{option.label}</span>
                  {sortField === option.value && (
                    <Check size={12} className='ml-auto text-blue-600' />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* File List */}
      <div className='flex-1 overflow-y-auto p-3 space-y-2'>
        {/* Select All - at top of file list */}
        {!loading && files.length > 0 && (
          <button
            onClick={() => {
              // 只选中/取消选中可选中的文件（排除处理中的）
              const selectableFileIds = selectableFiles.map(f => f.id);
              if (allSelectableSelected) {
                // 取消选中所有
                onFileSelectAll(false);
              } else {
                // 只选中可选中的文件
                onFileSelectAll(true, selectableFileIds);
              }
            }}
            className='w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200 bg-white'
          >
            <div
              className={`w-4 h-4 rounded border flex items-center justify-center ${
                allSelectableSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
              } ${someSelected ? 'bg-blue-100 border-blue-500' : ''}`}
            >
              {allSelectableSelected && <Check size={12} className='text-white' />}
              {someSelected && !allSelectableSelected && (
                <div className='w-2 h-2 bg-blue-500 rounded-sm' />
              )}
            </div>
            <span>{allSelectableSelected ? t('common.deselectAll') : t('common.selectAll')}</span>
          </button>
        )}

        {loading ? (
          <div className='flex items-center justify-center h-full'>
            <Loader2 className='text-gray-400 animate-spin' size={24} />
          </div>
        ) : files.length === 0 ? (
          <div className='flex flex-col items-center justify-center h-full text-center p-4'>
            <FileText className='text-gray-400 mb-2' size={32} />
            <p className='text-gray-500 text-sm'>{t('knowledge.files.noFiles')}</p>
          </div>
        ) : (
          files.map(file => {
            const isSelected = selectedFileIds.includes(file.id);
            // 检查本地轮询状态，如果没有则使用原始状态
            const pollingStatus = file.file_id ? fileProcessingStatus.get(file.file_id) : undefined;
            const isProcessing = pollingStatus === 'processing' || file.status === 'processing';
            const hasError = file.status === 'error' || pollingStatus === 'error';
            const isSelectable = !isProcessing && !hasError;

            return (
              <div
                key={file.id}
                className={`group relative overflow-hidden rounded-lg border transition-all ${
                  hasError
                    ? 'border-red-300 bg-red-50/30'
                    : isSelectable
                      ? 'cursor-pointer hover:shadow-sm'
                      : 'cursor-not-allowed opacity-75'
                } ${
                  isSelected && !hasError
                    ? 'bg-blue-50 border-blue-300'
                    : !hasError
                      ? 'bg-white border-gray-200 hover:border-gray-300'
                      : ''
                }`}
                onMouseEnter={() => {
                  if (hasError) {
                    setErrorTooltipFileId(file.id);
                  }
                }}
                onMouseLeave={() => {
                  setErrorTooltipFileId(null);
                }}
              >
                {/* Error indicator bar - top red accent */}
                {hasError && (
                  <div className='absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-red-600 z-10' />
                )}

                {/* Checkbox */}
                <div className='relative z-20'>
                  <div className='absolute top-3 left-3'>
                    {hasError ? (
                      // Error 状态显示 MessageSquareOff 图标，不可选中
                      <div className='flex items-center gap-1.5 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium'>
                        <MessageSquareOff size={14} />
                        <span>{t('knowledge.files.status.error')}</span>
                      </div>
                    ) : isProcessing ? (
                      // Processing 状态显示 loading 动画
                      <div className='w-5 h-5 flex items-center justify-center'>
                        <Loader2 size={16} className='animate-spin text-blue-500' />
                      </div>
                    ) : (
                      // 正常状态显示 checkbox
                      <div
                        className='w-4 h-4 rounded border flex items-center justify-center cursor-pointer bg-white group-hover:border-gray-400 border-gray-300'
                        onClick={e => {
                          e.stopPropagation();
                          onFileSelect(file.id, !isSelected);
                        }}
                      >
                        {isSelected && <Check size={12} className='text-blue-500' />}
                      </div>
                    )}
                  </div>
                </div>

                {/* Error Banner - positioned at bottom, doesn't block controls */}
                {hasError && errorTooltipFileId === file.id && (
                  <div className='absolute bottom-0 left-0 right-0 z-10'>
                    <div className='bg-red-600 text-white px-3 py-2 text-xs font-medium flex items-center justify-center gap-2 shadow-lg'>
                      <MessageSquareOff size={14} />
                      <span>{t('knowledge.files.parseError')}</span>
                    </div>
                  </div>
                )}

                {/* File Content */}
                <div
                  className={`relative z-0 ${hasError ? 'pb-10' : 'pb-3'} pl-8 pr-12 pt-3`}
                  onClick={() => {
                    if (isSelectable) {
                      onFileSelect(file.id, !isSelected);
                    }
                  }}
                >
                  {/* File Type Badge */}
                  <div
                    className={`w-10 h-10 ${getFileTypeColor(file.file_type)} rounded-lg flex items-center justify-center mb-2`}
                  >
                    <span className='text-xs font-semibold'>
                      {getFileTypeDisplay(file.file_type)}
                    </span>
                  </div>

                  {/* Title or File Name */}
                  <div
                    className={`text-sm font-medium line-clamp-2 mb-1 min-h-[2.5rem] ${
                      hasError ? 'text-red-900' : 'text-gray-900'
                    }`}
                  >
                    {file.title || file.name}
                  </div>

                  {/* Authors - show first 2, append "等N人" if more */}
                  {file.authors && file.authors.length > 0 && (
                    <div className={`text-xs mb-1 ${hasError ? 'text-red-700' : 'text-gray-600'}`}>
                      {file.authors.slice(0, 2).join(', ')}
                      {file.authors.length > 2 && `等${file.authors.length}人`}
                    </div>
                  )}

                  {/* Journal and Year */}
                  {(file.journal || file.year) && (
                    <div className={`text-xs mb-1 ${hasError ? 'text-red-700' : 'text-gray-600'}`}>
                      {file.journal && <span>{file.journal}</span>}
                      {file.journal && file.year && <span> · </span>}
                      {file.year && <span>{file.year}</span>}
                    </div>
                  )}

                  {/* Abstract - max 2 lines */}
                  {file.abstract && (
                    <div
                      className={`text-xs mb-2 line-clamp-2 ${
                        hasError ? 'text-red-700' : 'text-gray-500'
                      }`}
                    >
                      {file.abstract}
                    </div>
                  )}

                  {/* Keywords - horizontal scrollable tags */}
                  {file.keywords && file.keywords.length > 0 && (
                    <div className='flex gap-1 mb-2 overflow-x-auto scrollbar-thin'>
                      {file.keywords.slice(0, 4).map((keyword, index) => (
                        <span
                          key={index}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs whitespace-nowrap ${
                            hasError ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {keyword}
                        </span>
                      ))}
                      {file.keywords.length > 4 && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs whitespace-nowrap ${
                            hasError ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          +{file.keywords.length - 4}
                        </span>
                      )}
                    </div>
                  )}

                  {/* File Date and Size - aligned horizontally */}
                  <div className='flex items-center justify-between text-xs'>
                    <div className={hasError ? 'text-red-700' : 'text-gray-500'}>
                      {formatDateWithoutYear(file.upload_date)}
                    </div>
                    {file.size !== undefined && file.size !== null && (
                      <div className={hasError ? 'text-red-600' : 'text-gray-400'}>
                        {formatFileSize(file.size)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions - increased z-index to ensure it's always clickable */}
                <div className='absolute top-3 right-3 z-30'>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setHoveredFileId(hoveredFileId === file.id ? null : file.id);
                    }}
                    className={`p-1 rounded transition-colors ${
                      hasError ? 'hover:bg-red-200 bg-red-100/50' : 'hover:bg-gray-100'
                    }`}
                  >
                    <MoreVertical
                      size={14}
                      className={hasError ? 'text-red-700' : 'text-gray-500'}
                    />
                  </button>

                  {hoveredFileId === file.id && (
                    <div className='absolute top-6 right-0 z-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]'>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleDownloadClick(file);
                        }}
                        className='w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors'
                      >
                        <Download size={14} className='text-gray-500' />
                        <span>{t('common.download')}</span>
                      </button>
                      {knowledgeBase && canEditKnowledgeBase(knowledgeBase, user?.id) && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteClick(file);
                          }}
                          className='w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-50 transition-colors'
                          style={{
                            color: hasError ? '#dc2626' : '',
                            fontWeight: hasError ? 500 : '',
                          }}
                        >
                          <Trash2 size={14} />
                          <span>{t('common.delete')}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Upload Button */}
      {knowledgeBase && canEditKnowledgeBase(knowledgeBase, user?.id) && (
        <div className='p-3 border-t border-gray-200'>
          <button
            onClick={() => setShowUploadDialog(true)}
            className='w-full flex items-center justify-center gap-2 bg-blue-500 text-white px-4 py-2.5 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium'
          >
            <Plus size={16} />
            <span>{t('knowledge.files.addDocument')}</span>
          </button>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className='p-3 border-t border-gray-200'>
          <div className='flex items-center justify-between'>
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className='p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              <ChevronLeft size={16} className='text-gray-600' />
            </button>
            <span className='text-xs text-gray-600'>
              {pagination.page} / {pagination.pages}
            </span>
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className='p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              <ChevronRight size={16} className='text-gray-600' />
            </button>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      {showUploadDialog && knowledgeBase && (
        <FileUploadDialog
          knowledgeBaseId={knowledgeBase.id}
          existingFiles={files.map(f => ({ name: f.name, size: f.size || 0 }))}
          open={showUploadDialog}
          onClose={() => {
            setShowUploadDialog(false);
          }}
          onUploadSingleComplete={handleUploadSingleComplete}
          onUploadBatchComplete={handleUploadBatchComplete}
          onError={({ fileName, message }) => {
            toast.error(`${fileName}: ${message}`);
          }}
        />
      )}
    </div>
  );
};
